import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import "../styles/AddLeadModal.css";
import API_BASE_URL from "../config/env";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { parsePhoneNumber, CountryCode } from "libphonenumber-js";

interface Props {
    onClose: () => void;
    onLeadAdded?: () => void;
}

interface FormDataState {
    user_id: string;
    name: string;
    number: string;
    email: string;
    buying_location: string;
    areaType: string;
    configuration: string;
    budget_min: string;
    budget_max: string;
    relation_with_the_lead: string;
    other_relation: string;
    customer_address: string;
    remarks: string;
    voice_name?: string;
    zone?: string;
    country_code?: string;
}

interface FormErrors {
    [key: string]: string;
}

interface Zone {
    id: number;
    zone: string;
}

// Updated Zone ID to zone name mapping with new zones
const ZONE_ID_TO_NAME: { [key: number]: string } = {
    72: "GPC Pune East",
    73: "GPC Pune West",
    74: "GPC Bengaluru East",
    75: "GPC Bengaluru North",
    51: "GPC Mumbai",
    53: "GPC Dubai",
    66: "GPC Thane",
    65: "GPC Navi Mumbai",
    67: "GPC Hyderabad",
    70: "GPC Kolkata",
    82: "GPC Central Mumbai",
    83: "GPC South Mumbai",
};

// Updated Sub cities based on new zone IDs
const ZONE_SUB_CITIES: { [key: number]: string[] } = {
    72: [ // GPC Pune East
        "Kharadi",
        "Keshav Nagar",
        "Mundhwa",
        "Kalyani Nagar",
        "Koregaon Park",
        "Viman Nagar",
        "Hadapsar",
        "Wagholi",
        "Dhanori"
    ],
    73: [ // GPC Pune West
        "Warje",
        "Kothrud",
        "Bavdhan",
        "Pashan",
        "Baner",
        "Balewadi",
        "Sus",
        "Mahalunge",
        "Wakad",
        "Hinjewadi Phase 1",
        "Hinjewadi Phase 2",
        "Hinjewadi Phase 3",
        "Tathawade",
        "Punawale",
        "Ravet",
        "Kiwale",
        "PCMC"
    ],
    74: [ // GPC Bengaluru East
        "Sarjapur Road",
        "Whitefield",
        "Gunjur",
        "Varthur",
        "Hosa Road",
        "Central Jail Road",
        "Buddigere Cross",
        "Hoskotte"
    ],
    75: [ // GPC Bengaluru North
        "Hebbal",
        "Thanisandra",
        "Sadahalli",
        "Shettygere",
        "Yelahanka",
        "Satanur",
        "KIADB Baglur",
        "IVC Road"
    ],
    51: [ // GPC Mumbai
        "Andheri",
        "Bandra",
        "Powai",
        "Borivali",
        "Dadar",
        "Lower Parel",
        "Goregaon",
        "Chembur",
        "Malad",
        "Kandivali"
    ],
    53: [ // GPC Dubai
        "Downtown Dubai",
        "Dubai Marina",
        "Business Bay",
        "Jumeirah Village Circle (JVC)",
        "Palm Jumeirah",
        "Arabian Ranches",
        "Deira",
        "Al Barsha",
        "Dubai Silicon Oasis",
        "Jumeirah Lake Towers (JLT)"
    ],
    66: [ // GPC Thane
        "Thane West",
        "Thane East",
        "Majiwada",
        "Kolshet",
        "Manpada",
        "Wagle Estate",
        "Vartak Nagar",
        "Naupada",
        "Panchpakhadi"
    ],
    65: [ // GPC Navi Mumbai
        "Vashi",
        "Kharghar",
        "Nerul",
        "CBD Belapur",
        "Seawoods",
        "Sanpada",
        "Turbhe",
        "Ghansoli",
        "Airoli",
        "Kamothe"
    ],
    67: [ // GPC Hyderabad
        "Trilight (Kokapet)",
        "Rise with 9 (Neopolis)",
        "Sattva Lago (Neopolis)",
        "Ramky Villa's (Shankarpally)",
        "Ramky The Eminent (Rajendra Nagar)",
        "Casagrand Vybe (Rajendra Nagar)",
        "Gachibowli",
        "HITEC City",
        "Banjara Hills",
        "Jubilee Hills",
        "Kondapur",
        "Madhapur",
        "Kukatpally",
        "Begumpet",
        "Secunderabad"
    ],
    70: [ // GPC Kolkata
        "New Town",
        "Salt Lake City",
        "Howrah",
        "Rajarhat",
        "Madhyamgram",
        "Embypass",
        "Tollygunge",
        "Joka",
        "Behala",
        "E.M. Bypass",
        "Alipore",
        "Park Street",
        "Ballygunge"
    ],
    82: [ // GPC Central Mumbai
        "Mulund",
        "Bhandup",
        "Vikroli",
        "Kanjurmarg"
    ],
    83: [ // GPC South Mumbai
        "Wadala", "Matunga", "Shweri", "Byculla", "worli", "Lalbaugh"
    ]
};

const AREA_TYPES: { [key: string]: string[] } = {
    residential: ["1BHK", "2BHK", "3BHK", "4BHK"],
    commercial: ["Office Space", "Retail Shop"],
    industrial: ["Factory", "Warehouse"],
};

const RELATION_OPTIONS = [
    { value: "", label: "Select" },
    { value: "relative", label: "Relative" },
    { value: "friend", label: "Friend" },
    { value: "office colleague", label: "Office Colleague" },
    { value: "other", label: "Other" },
];

// Country code validation rules
const COUNTRY_VALIDATION: { [key: string]: { code: CountryCode; digitLength: number; regex: RegExp; example: string } }
    = {
    IN: { code: "IN", digitLength: 10, regex: /^[6-9]\d{9}$/, example: "9876543210" },
    AE: { code: "AE", digitLength: 9, regex: /^[5]\d{8}$/, example: "501234567" },
    US: { code: "US", digitLength: 10, regex: /^[2-9]\d{9}$/, example: "2125551234" },
    GB: { code: "GB", digitLength: 10, regex: /^[1-9]\d{9}$/, example: "7123456789" },
    AU: { code: "AU", digitLength: 9, regex: /^[4]\d{8}$/, example: "412345678" },
    CA: { code: "CA", digitLength: 10, regex: /^[2-9]\d{9}$/, example: "4165551234" },
    SG: { code: "SG", digitLength: 8, regex: /^[689]\d{7}$/, example: "91234567" },
    MY: { code: "MY", digitLength: 10, regex: /^[1]\d{9}$/, example: "1234567890" },
};

const AddLeadModal: React.FC<Props> = ({ onClose, onLeadAdded }) => {
    const [formData, setFormData] = useState<FormDataState>({
        user_id: "",
        name: "",
        number: "",
        email: "",
        buying_location: "",
        areaType: "",
        configuration: "",
        budget_min: "",
        budget_max: "",
        relation_with_the_lead: "",
        other_relation: "",
        customer_address: "",
        remarks: "",
        voice_name: "",
        zone: "",
        country_code: "IN",
    });

    const [selectedCountry, setSelectedCountry] = useState<CountryCode>("IN");
    const [voiceNote, setVoiceNote] = useState<File | null>(null);
    const [isBlocked, setIsBlocked] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errors, setErrors] = useState<FormErrors>({});
    const [zones, setZones] = useState<Zone[]>([]);
    const [loadingZones, setLoadingZones] = useState(false);
    const [phoneNumberError, setPhoneNumberError] = useState("");
    const [cleanPhoneNumber, setCleanPhoneNumber] = useState<string>("");

    const token = localStorage.getItem("authToken");

    // Get zone name from ID
    const getZoneNameFromId = (zoneId: number): string => {
        return ZONE_ID_TO_NAME[zoneId] || "";
    };

    // Get available sub cities based on selected zone ID
    const getAvailableSubCities = useCallback(() => {
        const zoneId = Number(formData.zone);
        return ZONE_SUB_CITIES[zoneId] || [];
    }, [formData.zone]);

    // Function to validate phone number based on country
    const validatePhoneNumber = useCallback((phoneNumber: string, countryCode: CountryCode): {
        isValid: boolean; message: string; cleanNumber: string
    } => {
        if (!phoneNumber) {
            return { isValid: false, message: "Mobile number is required", cleanNumber: "" };
        }

        try {
            const parsedNumber = parsePhoneNumber(phoneNumber, countryCode);

            if (!parsedNumber || !parsedNumber.isValid()) {
                return {
                    isValid: false, message: `Invalid ${getCountryLabel(countryCode)} phone number`,
                    cleanNumber: ""
                };
            }

            const nationalNumber = parsedNumber.nationalNumber;
            const validationRule = COUNTRY_VALIDATION[countryCode];

            if (validationRule) {
                if (!validationRule.regex.test(nationalNumber)) {
                    return {
                        isValid: false,
                        message: `Invalid ${getCountryLabel(countryCode)} mobile number format. Example:
                            ${validationRule.example}`,
                        cleanNumber: nationalNumber
                    };
                }
            }

            return { isValid: true, message: "", cleanNumber: nationalNumber };
        } catch (error) {
            return {
                isValid: false, message: `Invalid phone number format for
                            ${getCountryLabel(countryCode)}`, cleanNumber: ""
            };
        }
    }, []);

    const getCountryLabel = (countryCode: CountryCode): string => {
        const labels: { [key: string]: string } = {
            IN: "Indian",
            AE: "UAE",
            US: "US",
            GB: "UK",
            AU: "Australian",
            CA: "Canadian",
            SG: "Singapore",
            MY: "Malaysian",
        };
        return labels[countryCode] || countryCode;
    };

    useEffect(() => {
        fetchZones();
    }, []);

    const fetchZones = async () => {
        if (!token) {
            console.warn("No token found for fetching zones");
            Swal.fire({
                icon: 'warning',
                title: 'Authentication Required',
                text: 'Please login again to fetch zones',
                confirmButtonColor: '#3085d6',
            });
            return;
        }

        setLoadingZones(true);
        try {
            const response = await axios.get(
                `${API_BASE_URL}/api/global-partner-connect/zone-list`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    timeout: 15000,
                }
            );

            if (response.data && response.data.status === true && response.data.data) {
                setZones(response.data.data);
            } else {
                console.error("Failed to fetch zones - Invalid response structure:", response.data);
                Swal.fire({
                    icon: 'warning',
                    title: 'Zones Not Available',
                    text: response.data?.message || 'Invalid response from server',
                    confirmButtonColor: '#3085d6',
                });
            }
        } catch (error: any) {
            console.error("Error fetching zones:", error);

            let errorMessage = 'Unable to fetch zones list. Please check your connection.';

            if (error.response) {
                errorMessage = error.response.data?.message || `Server error: ${error.response.status}`;
            } else if (error.request) {
                errorMessage = "No response from server. Please check your internet connection.";
            } else {
                errorMessage = error.message || "Failed to fetch zones";
            }

            Swal.fire({
                icon: 'error',
                title: 'Failed to Load Zones',
                text: errorMessage,
                confirmButtonColor: '#3085d6',
            });
        } finally {
            setLoadingZones(false);
        }
    };

    useEffect(() => {
        try {
            const user = localStorage.getItem("user");
            if (user) {
                const parsedUser = JSON.parse(user);
                setFormData((prev) => ({
                    ...prev,
                    user_id: parsedUser.id || "",
                }));
            }
        } catch (error) {
            console.error("Error parsing user data:", error);
        }
    }, []);

    useEffect(() => {
        return () => {
            if (voiceNote) {
                const url = URL.createObjectURL(voiceNote);
                URL.revokeObjectURL(url);
            }
        };
    }, [voiceNote]);

    const validateForm = useCallback((): boolean => {
        const newErrors: FormErrors = {};

        if (!formData.number) {
            newErrors.number = "Mobile number is required";
        } else {
            const validation = validatePhoneNumber(formData.number, selectedCountry);
            if (!validation.isValid) {
                newErrors.number = validation.message;
                setPhoneNumberError(validation.message);
            } else {
                setPhoneNumberError("");
                setCleanPhoneNumber(validation.cleanNumber);
            }
        }

        if (!formData.name.trim()) {
            newErrors.name = "Full name is required";
        } else if (formData.name.trim().length < 2) {
            newErrors.name = "Name must be at least 2 characters long";
        } if (!formData.zone) {
            newErrors.zone = "Buying location is required";
        } if (!formData.buying_location.trim()) {
            newErrors.buying_location = "Sub city / Zone is required";
        } if (formData.email &&
            !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = "Invalid email format";
        } if (formData.budget_min &&
            formData.budget_max) {
                const min = parseFloat(formData.budget_min); const
                    max = parseFloat(formData.budget_max); if (!isNaN(min) && !isNaN(max) && min > max) {
                        newErrors.budget_max = "Maximum budget should be greater than minimum budget";
                    }
        }

        if (!formData.remarks) {
            newErrors.remarks = "Remarks are required";
        } else if (formData.remarks.length < 20) {
            newErrors.remarks = "Remarks must be at least 20 characters long";
        }
        setErrors(newErrors); return Object.keys(newErrors).length === 0;
    }, [formData,
        selectedCountry, validatePhoneNumber]); const handleChange = useCallback((e:
            React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
            const { name, value } = e.target;

            setFormData((prev) => {
                if (name === "zone") {
                    return { ...prev, [name]: value, buying_location: "" };
                }
                if (name === "areaType") {
                    return { ...prev, [name]: value, configuration: "" };
                }
                return { ...prev, [name]: value };
            });

            setErrors((prev) => {
                if (!prev[name]) return prev;
                const next = { ...prev };
                delete next[name];
                return next;
            });
        },
            []
        );

    const handlePhoneChange = useCallback((value: string | undefined) => {
        const phoneValue = value || "";

        setFormData((prev) => ({
            ...prev,
            number: phoneValue,
        }));

        setPhoneNumberError("");
        setErrors((prev) => {
            const next = { ...prev };
            delete next.number;
            return next;
        });

        if (phoneValue && phoneValue.length > 3) {
            const validation = validatePhoneNumber(phoneValue, selectedCountry);
            if (validation.isValid && validation.cleanNumber.length ===
                COUNTRY_VALIDATION[selectedCountry]?.digitLength) {
                setCleanPhoneNumber(validation.cleanNumber);
                handleMobileCheck(validation.cleanNumber);
            } else if (!validation.isValid && phoneValue.length > 5) {
                setPhoneNumberError(validation.message);
                setErrors((prev) => ({ ...prev, number: validation.message }));
            }
        }
    }, [selectedCountry, validatePhoneNumber]);

    const handleCountryChange = useCallback((country: CountryCode | undefined) => {
        const newCountry = country || "IN";
        setSelectedCountry(newCountry);
        setFormData((prev) => ({
            ...prev,
            country_code: newCountry,
        }));
        setPhoneNumberError("");
        setErrors((prev) => {
            const next = { ...prev };
            delete next.number;
            return next;
        });
        setIsBlocked(false);
        setCleanPhoneNumber("");
    }, []);

    const handleMobileCheck = async (mobile: string) => {
        if (!token) {
            console.warn("Token not found for mobile check");
            return;
        }

        try {
            const response = await axios.get(
                `${API_BASE_URL}/api/global-partner-connect/check-lead-exists`,
                {
                    params: { mobile_number: mobile },
                    headers: { Authorization: `Bearer ${token}` },
                    timeout: 10000,
                }
            );

            if (response.data.status === "success") {
                setIsBlocked(false);
                setErrors((prev) => {
                    const next = { ...prev };
                    delete next.number;
                    return next;
                });
                setPhoneNumberError("");
            } else {
                setIsBlocked(true);
                const errorMsg = response.data.message || "This mobile number is already  registered";
                setPhoneNumberError(errorMsg);
                setErrors((prev) => ({ ...prev, number: errorMsg }));
            }
        } catch (error: any) {
            const errMsg = error?.response?.data?.message;
            if (errMsg && errMsg.includes("already exists")) {
                setIsBlocked(true);
                setPhoneNumberError(errMsg);
                setErrors((prev) => ({ ...prev, number: errMsg }));
            } else {
                console.warn("Mobile check failed:", error?.message || error);
                setIsBlocked(false);
            }
        }
    };

    const handleVoiceNoteUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            const allowedTypes = ['audio/mpeg', 'audio/wav', 'audio/m4a', 'audio/ogg',
                'audio/webm'];
            if (!allowedTypes.includes(file.type)) {
                Swal.fire({
                    icon: 'error',
                    title: 'Invalid File Type',
                    text: 'Please upload MP3, WAV, M4A, OGG, or WEBM audio files only.',
                    confirmButtonColor: '#3085d6',
                });
                e.target.value = '';
                return;
            }

            if (file.size > 10 * 1024 * 1024) {
                Swal.fire({
                    icon: 'error',
                    title: 'File Too Large',
                    text: 'Please upload audio files smaller than 10MB.',
                    confirmButtonColor: '#3085d6',
                });
                e.target.value = '';
                return;
            }

            setVoiceNote(file);
            setFormData((prev) => ({ ...prev, voice_name: file.name }));
        }
    };

    const removeVoiceNote = () => {
        setVoiceNote(null);
        setFormData((prev) => ({ ...prev, voice_name: "" }));

        const fileInput = document.getElementById('voice_note') as HTMLInputElement;
        if (fileInput) {
            fileInput.value = '';
        }
    };

    const retryFetchZones = () => {
        fetchZones();
    };

    const showValidationErrors = () => {
        const errorFields: { [key: string]: string } = {
            number: 'Mobile Number',
            name: 'Full Name',
            zone: 'Buying Location',
            buying_location: 'Sub City / Zone',
            email: 'Email',
            budget_max: 'Budget Range',
            remarks: 'Remarks'
        };

        let errorHtml = '<div style="text-align: left;"><strong>Please fix the following  errors:</strong><br /><br />';

        Object.entries(errors).forEach(([field, message]) => {
            const fieldName = errorFields[field] || field.replace(/_/g, '').replace(/\b\w/g, l => l.toUpperCase());
            errorHtml += ` <div  style="margin-bottom: 8px; padding: 4px 8px; border-left: 3px solid #d9534f; background: #f8f8f8;"> <strong>• ${fieldName}:</strong> ${message}</div>
                                            `;
        });

        errorHtml += '</div>';

        Swal.fire({
            icon: 'warning',
            title: 'Validation Error',
            html: errorHtml,
            confirmButtonColor: '#3085d6',
            confirmButtonText: 'OK',
            width: '500px',
        });
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) {
            showValidationErrors();

            const firstErrorField = Object.keys(errors)[0];
            if (firstErrorField) {
                const el = document.getElementsByName(firstErrorField)[0] as HTMLElement |
                    undefined;
                if (el) el.focus();
            }
            return;
        }

        if (isBlocked) {
            Swal.fire({
                icon: 'error',
                title: 'Lead Already Exists',
                text: 'This lead already exists. Cannot submit.',
                confirmButtonColor: '#3085d6',
            });
            return;
        }

        if (!token) {
            Swal.fire({
                icon: 'error',
                title: 'Authentication Required',
                text: 'Authentication token not found',
                confirmButtonColor: '#3085d6',
            });
            return;
        }

        setIsSubmitting(true);

        try {
            const submitData = new FormData();

            let phoneNumberToSubmit = cleanPhoneNumber;
            if (!phoneNumberToSubmit) {
                const validation = validatePhoneNumber(formData.number, selectedCountry);
                phoneNumberToSubmit = validation.isValid ? validation.cleanNumber : "";
            }

            const submissionData: any = {
                user_id: formData.user_id,
                name: formData.name,
                number: phoneNumberToSubmit,
                country_code: selectedCountry,
                email: formData.email,
                zone: formData.zone,
                buying_location: formData.buying_location,
                areaType: formData.areaType,
                configuration: formData.configuration,
                budget_min: formData.budget_min || "0",
                budget_max: formData.budget_max || "0",
                relation_with_the_lead: formData.relation_with_the_lead,
                other_relation: formData.other_relation || "",
                customer_address: formData.customer_address || "",
                remarks: formData.remarks,
                voice_name: formData.voice_name || "",
            };

            Object.entries(submissionData).forEach(([key, value]) => {
                if (value !== undefined && value !== null && value !== "") {
                    submitData.append(key, value.toString());
                }
            });

            if (voiceNote) {
                submitData.append("voice_note", voiceNote);
            }

            const response = await axios.post(
                `${API_BASE_URL}/api/global-partner-connect/add-raw-lead-global-partner`,
                submitData,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "multipart/form-data",
                    },
                    timeout: 30000,
                }
            );

            console.log("Lead added successfully:", response.data);

            await Swal.fire({
                icon: 'success',
                title: 'Success!',
                text: 'Lead Added Successfully',
                confirmButtonColor: '#3085d6',
                timer: 2000,
                showConfirmButton: true,
            });

            onLeadAdded?.();
            onClose();
        } catch (error: any) {
            console.error("Error adding lead:", error);
            const errorMsg = error?.response?.data?.message || error?.message || "Failed to sadd lead.Please try again.";

            await Swal.fire({
                icon: 'error',
                title: 'Submission Failed',
                text: errorMsg,
                confirmButtonColor: '#3085d6',
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    const isFormDisabled = isBlocked || isSubmitting;

    return (
        <div className="modal-overlay" onClick={handleOverlayClick}>
            <div className="modal-container">
                <form onSubmit={handleSubmit} noValidate>
                    <div className="modal-header">
                        <h3>Add New Lead</h3>
                        <button type="button" className="modal-close"
                            onClick={onClose} disabled={isSubmitting}
                            aria-label="Close modal">
                            ✕
                        </button>
                    </div>

                    <div className="modal-body">
                        <div className="form-grid">
                            {/* Mobile Number with Country Code */}
                            <div className="form-group">
                                <label htmlFor="number">Mobile Number *</label>
                                <PhoneInput international defaultCountry="IN"
                                    placeholder="Enter phone number"
                                    value={formData.number}
                                    onChange={handlePhoneChange}
                                    onCountryChange={handleCountryChange}
                                    disabled={isSubmitting} className={errors.number
                                        ? "phone-input-error" : ""} />
                                {phoneNumberError && <span
                                    className="error-text">{phoneNumberError}</span>}
                            </div>

                            {/* Full Name */}
                            <div className="form-group">
                                <label htmlFor="name">Full Name *</label>
                                <input id="name" type="text" name="name"
                                    placeholder="Enter full name"
                                    value={formData.name} onChange={handleChange}
                                    disabled={isFormDisabled} required
                                    className={errors.name ? "input-error" : ""}
                                    minLength={2} />
                                {errors.name && <span
                                    className="error-text">{errors.name}</span>}
                            </div>

                            {/* Email */}
                            <div className="form-group">
                                <label htmlFor="email">Email</label>
                                <input id="email" type="email" name="email"
                                    placeholder="email@example.com"
                                    value={formData.email} onChange={handleChange}
                                    disabled={isFormDisabled}
                                    className={errors.email ? "input-error" : ""
                                    } />
                                {errors.email && <span
                                    className="error-text">{errors.email}</span>}
                            </div>

                            {/* Zone Selection */}
                            <div className="form-group">
                                <label htmlFor="zone">Buying Location *</label>
                                <select id="zone" name="zone" value={formData.zone}
                                    onChange={handleChange} disabled={isSubmitting
                                        || loadingZones} className={errors.zone
                                            ? "input-error" : ""} required>
                                    <option value="">Select Buying Location</option>
                                    {zones.map((zone) => (
                                        <option key={zone.id} value={zone.id}>
                                            {zone.zone}
                                        </option>
                                    ))}
                                </select>
                                {errors.zone && <span
                                    className="error-text">{errors.zone}</span>}
                                {loadingZones && <small
                                    className="helper-text">Loading
                                    zones...</small>}
                                {!loadingZones && zones.length === 0 && (
                                    <div style={{ marginTop: '8px' }}>
                                        <small className="error-text">No zones
                                            available. </small>
                                        <button type="button" onClick={retryFetchZones}
                                            className="retry-btn">
                                            Retry
                                        </button>
                                    </div>
                                )}
                            </div>

                            {/* Sub City / Zone based on selected zone */}
                            {formData.zone && (
                                <div className="form-group">
                                    <label htmlFor="buying_location">Sub City / Zone
                                        *</label>
                                    <select id="buying_location" name="buying_location"
                                        value={formData.buying_location}
                                        onChange={handleChange} required
                                        disabled={isFormDisabled}
                                        className={errors.buying_location
                                            ? "input-error" : ""}>
                                        <option value="">Select Sub City / Zone</option>
                                        {getAvailableSubCities().map((zone) => (
                                            <option key={zone} value={zone}>
                                                {zone}
                                            </option>
                                        ))}
                                    </select>
                                    {errors.buying_location && <span
                                        className="error-text">{errors.buying_location}</span>}
                                    {getAvailableSubCities().length === 0 && (
                                        <small className="helper-text"
                                            style={{ color: 'orange' }}>
                                            No sub cities available for selected location
                                        </small>
                                    )}
                                    {getAvailableSubCities().length > 0 && (
                                        <small className="helper-text">
                                            Select specific area in
                                            {getZoneNameFromId(Number(formData.zone))}
                                        </small>
                                    )}
                                </div>
                            )}

                            {/* Property Type */}
                            <div className="form-group">
                                <label htmlFor="areaType">Property Type</label>
                                <select id="areaType" name="areaType"
                                    value={formData.areaType}
                                    onChange={handleChange} disabled={isSubmitting}>
                                    <option value="">Select Property Type</option>
                                    {Object.keys(AREA_TYPES).map((key) => (
                                        <option key={key} value={key}>
                                            {key.charAt(0).toUpperCase() + key.slice(1)}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Configuration */}
                            {formData.areaType && (
                                <div className="form-group">
                                    <label htmlFor="configuration">Configuration</label>
                                    <select id="configuration" name="configuration"
                                        value={formData.configuration}
                                        onChange={handleChange} disabled={isSubmitting}>
                                        <option value="">Select Configuration</option>
                                        {AREA_TYPES[formData.areaType]?.map((option) =>
                                        (
                                            <option key={option} value={option}>
                                                {option}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            {/* Budget */}
                            <div className="form-group budget-group">
                                <label>Budget (Lakhs)</label>
                                <div className="budget-inputs">
                                    <input type="number" name="budget_min"
                                        placeholder="Min"
                                        value={formData.budget_min}
                                        onChange={handleChange}
                                        disabled={isSubmitting} min="0"
                                        step="0.1" />
                                    <span className="budget-separator">-</span>
                                    <input type="number" name="budget_max"
                                        placeholder="Max"
                                        value={formData.budget_max}
                                        onChange={handleChange}
                                        disabled={isSubmitting} min="0" step="0.1"
                                        className={errors.budget_max ? "input-error"
                                            : ""} />
                                </div>
                                {errors.budget_max && <span
                                    className="error-text">{errors.budget_max}</span>}
                            </div>

                            {/* Relation with Lead */}
                            <div className="form-group">
                                <label htmlFor="relation_with_the_lead">Relation
                                    with Lead</label>
                                <select id="relation_with_the_lead"
                                    name="relation_with_the_lead"
                                    value={formData.relation_with_the_lead}
                                    onChange={handleChange} disabled={isSubmitting}>
                                    {RELATION_OPTIONS.map((option) => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Other Relation */}
                            {formData.relation_with_the_lead === "other" && (
                                <div className="form-group">
                                    <label htmlFor="other_relation">Specify Relation
                                        *</label>
                                    <input id="other_relation" name="other_relation"
                                        placeholder="Specify your relation"
                                        value={formData.other_relation}
                                        onChange={handleChange}
                                        disabled={isSubmitting} />
                                </div>
                            )}

                            {/* Voice Note Upload */}
                            <div className="form-group full-width">
                                <label>Recording Upload</label>
                                <div className="voice-note-upload-wrapper">
                                    <div className="file-upload-container">
                                        <input type="file" id="voice_note"
                                            name="voice_note" accept="audio/*"
                                            onChange={handleVoiceNoteUpload}
                                            disabled={isSubmitting}
                                            className="file-input-hidden" />
                                        <label htmlFor="voice_note"
                                            className="file-upload-btn">
                                            <span className="upload-icon">📁</span>
                                            <span>Choose Audio File</span>
                                        </label>
                                        <span className="file-types-text">MP3, WAV,
                                            M4A, OGG, WEBM (Max 10MB)</span>
                                    </div>

                                    {voiceNote && (
                                        <div className="file-info-card">
                                            <div className="file-details">
                                                <span className="file-icon">🎵</span>
                                                <span
                                                    className="file-name-text">{voiceNote.name}</span>
                                                <span className="file-size-text">
                                                    ({(voiceNote.size / 1024 /
                                                        1024).toFixed(2)} MB)
                                                </span>
                                                <button type="button"
                                                    className="remove-file-btn"
                                                    onClick={removeVoiceNote}
                                                    disabled={isSubmitting}
                                                    title="Remove file">
                                                    ✕
                                                </button>
                                            </div>
                                            <audio controls
                                                className="audio-preview-player">
                                                <source
                                                    src={URL.createObjectURL(voiceNote)}
                                                    type={voiceNote.type} />
                                                Your browser does not support the audio
                                                element.
                                            </audio>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Remarks */}
                            <div className="form-group full-width">
                                <label htmlFor="remarks">Remarks *</label>
                                <textarea id="remarks" name="remarks" rows={3}
                                    placeholder="Additional remarks..."
                                    value={formData.remarks} onChange={handleChange}
                                    disabled={isSubmitting}
                                    className={errors.remarks ? "input-error" : ""
                                    } />
                                {errors.remarks && <span
                                    className="error-text">{errors.remarks}</span>}
                                <small className="helper-text"
                                    style={{ color: formData.remarks.length < 20 ? "red" : "green" }}>
                                    {formData.remarks.length}/20 characters minimum
                                </small>
                            </div>

                            {/* Customer Address */}
                            <div className="form-group full-width">
                                <label htmlFor="customer_address">Customer
                                    Address</label>
                                <textarea id="customer_address"
                                    name="customer_address" rows={3}
                                    placeholder="Enter customer's address..."
                                    value={formData.customer_address}
                                    onChange={handleChange}
                                    disabled={isSubmitting} />
                            </div>
                        </div>
                    </div>

                    <div className="modal-actions">
                        <button type="button" className="cancel-btn"
                            onClick={onClose} disabled={isSubmitting}>
                            Cancel
                        </button>
                        <button type="submit" className="add-btn-modal"
                            disabled={isBlocked || isSubmitting}>
                            {isSubmitting ? (
                                <>
                                    <span className="loading-spinner"></span>
                                    Adding Lead...
                                </>
                            ) : (
                                "Add Lead"
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AddLeadModal;